// Les commentaires d'une publication.
//
// Un seul niveau de réponse, comme sur Facebook : répondre à une réponse
// rattache au même fil plutôt que de creuser à l'infini. Une conversation qui
// s'enfonce sur cinq niveaux devient illisible sur un téléphone, et personne
// n'y répond plus.
//
// L'abonnement ne s'installe QUE lorsque les commentaires sont ouverts : dans un
// fil de trente publications, écouter les trente d'avance coûterait trente
// lectures continues pour ce que personne ne regarde.

import { useEffect, useRef, useState } from "react";
import Store from "../store.js";
import { ilYA, toast, confirmer } from "../ui.js";
import Avatar from "./Avatar.jsx";
import Certifie from "./Certifie.jsx";
import Icone from "./Icone.jsx";
import TexteRiche from "./TexteRiche.jsx";
import { useAbonnement, useMembre } from "./hooks.js";
import { useMentions, ListeMentions } from "./Mentions.jsx";

/** Une bulle. `enfant` décale la réponse sous son parent. */
function Bulle({ c, ctx, enfant, effacable, onRepondre, onSupprimer, onAmen, mienne }) {
  const auteur = useMembre(ctx, c.authorId, { nom: c.auteurNom, photo: c.auteurPhoto });
  return (
    <div className={`commentaire${enfant ? " commentaire--reponse" : ""}`}>
      <Avatar membre={auteur} taille={enfant ? 26 : 32} />
      <div className="commentaire__bulle">
        <a href={`#/profil/${c.authorId}`}>
          <strong>
            {auteur.nom}
            <Certifie membre={auteur} taille={12} />
          </strong>
        </a>
        <p>
          <TexteRiche texte={c.texte} mentions={c.mentions} />
        </p>
        <span className="commentaire__pied">
          <span className="commentaire__date">{ilYA(c.createdAt)}</span>
          {/* ⚠️ RÉAGIR À UN COMMENTAIRE — 24 août 2026, demandé par Josaphat.
              Il a fallu lever `allow update: if false` sur les commentaires,
              qui les protégeait de TOUTE modification — mais seulement pour le
              compteur : le texte reste intouchable par tout le monde, son
              auteur compris. Onze attaques au banc le vérifient.

              ⚠️ LE COMPTE VIENT DU DOCUMENT, L'ÉTAT DU TIROIR PERSONNEL. Le
              premier peut être gonflé par n'importe quel membre et ne trompe
              alors que l'affichage ; le second fait foi, et c'est lui qui
              allume ce bouton. */}
          <button
            className={`commentaire__repondre${mienne ? " est-actif" : ""}`}
            onClick={() => onAmen?.(c)}
          >
            🙏 {c.amens ? c.amens : "Amen"}
          </button>
          <button className="commentaire__repondre" onClick={() => onRepondre(c)}>
            Répondre
          </button>
        </span>
      </div>
      {effacable && (
        <button className="icon-btn icon-btn--mini" aria-label="Supprimer" onClick={() => onSupprimer(c)}>
          <Icone nom="fermer" taille={13} />
        </button>
      )}
    </div>
  );
}

/**
 * Range les commentaires en fils d'un niveau.
 *
 * Une réponse dont le parent a été supprimé remonte au premier niveau : sans
 * cela elle disparaîtrait de l'affichage tout en restant en base, et son auteur
 * croirait l'avoir perdue.
 */
function enFils(cs) {
  const parents = cs.filter((c) => !c.repondA);
  const enfants = new Map();
  for (const c of cs) {
    if (!c.repondA) continue;
    if (!enfants.has(c.repondA)) enfants.set(c.repondA, []);
    enfants.get(c.repondA).push(c);
  }
  for (const id of [...enfants.keys()]) {
    if (!parents.some((p) => p.id === id)) parents.push(...enfants.get(id));
  }
  return {
    parents: parents.sort((a, b) => a.createdAt - b.createdAt),
    enfants,
  };
}

export default function Commentaires({ post, ctx }) {
  const commentaires = useAbonnement((cb) => Store.comments.subscribe(post.id, cb), null, [post.id]);
  const [texte, setTexte] = useState("");

  /* ⚠️ UNE SEULE ÉCOUTE POUR TOUT LE FIL. Un commentaire en compte parfois
     trente : demander pour chacun « ai-je réagi ? » ferait trente lectures à
     chaque ouverture. Le tiroir se lit en entier, une fois. */
  const mesAmens = useAbonnement(
    (cb) => Store.comments.subscribeMesAmens?.(cb),
    new Set(),
    []
  ) || new Set();
  const [poses, setPoses] = useState(() => new Set());

  /* ⚠️ L'ÉCHO DU SERVEUR REMET L'OPTIMISME À ZÉRO — 20 octobre 2026. `poses`
     n'était jamais vidé : quand l'écoute confirmait la réaction, le OU
     exclusif de `aReagi` basculait et le bouton affichait l'INVERSE — éteint
     alors que l'amen tenait, allumé pendant qu'un second appui le retirait.
     Trouvé à l'audit. Dès que `mesAmens` ré-émet, la vérité du serveur inclut
     le geste : la correction locale n'a plus rien à corriger. */
  useEffect(() => setPoses(new Set()), [mesAmens]);

  /* ⚠️ ON MONTRE AVANT DE SAVOIR. Sur une connexion hésitante, attendre le
     serveur ferait un bouton qui ne répond pas — c'est ainsi qu'on appuie
     trois fois. L'écoute ci-dessus rétablit la vérité, refus compris. */
  const amen = async (c) => {
    setPoses((v) => {
      const n = new Set(v);
      if (n.has(c.id)) n.delete(c.id); else n.add(c.id);
      return n;
    });
    try {
      await Store.comments.reagir(post.id, c.id);
    } catch (err) {
      toast(err.message || "Réaction impossible", "erreur");
    }
  };

  /* La vérité du serveur, corrigée de ce qu'on vient de faire à l'instant. */
  const aReagi = (id) => (poses.has(id) ? !mesAmens.has(id) : mesAmens.has(id));
  const [reponseA, setReponseA] = useState(null); // { id, nom }
  const champ = useRef(null);
  const mentions = useMentions(texte, setTexte, ctx);

  const { parents, enfants } = enFils(commentaires || []);

  // Le commentaire visé a pu disparaître entre-temps : on ne répond pas dans le
  // vide, la réponse deviendrait un commentaire orphelin sans qu'on le dise.
  useEffect(() => {
    if (reponseA && commentaires && !commentaires.some((c) => c.id === reponseA.id)) setReponseA(null);
  }, [commentaires, reponseA]);

  useEffect(() => {
    champ.current?.focus();
  }, []);

  const ajuster = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(120, el.scrollHeight) + "px";
  };

  const viser = (c) => {
    /* ⚠️ RÉPONDRE À UNE RÉPONSE RESTE DANS LE MÊME FIL — 20 octobre 2026,
       trouvé à l'audit. Viser `c.id` quand `c` est déjà une réponse créait un
       commentaire rattaché à un identifiant que `enFils` ne connaît pas comme
       parent : la réponse partait s'afficher toute seule, au premier niveau —
       le contraire de ce que promet l'en-tête de ce fichier. On vise le haut
       du fil ; le NOM, lui, reste celui qu'on cite. */
    setReponseA({ id: c.repondA || c.id, nom: c.auteurNom || "Membre" });
    champ.current?.focus();
  };

  const supprimer = async (c) => {
    if (await confirmer("Supprimer ce commentaire ?", "Action définitive.", "Supprimer"))
      await Store.comments.remove(post.id, c.id);
  };

  const envoyer = async (e) => {
    e?.preventDefault();
    const t = texte.trim();
    if (!t) return;
    setTexte("");
    ajuster(champ.current);
    const cible = reponseA;
    const citees = mentions.mentions();
    setReponseA(null);
    mentions.oublier();
    try {
      await Store.comments.add(post.id, {
        authorId: ctx.moi.uid,
        auteurNom: ctx.moi.nom,
        auteurPhoto: ctx.moi.photo || "",
        texte: t,
        mentions: citees,
        repondA: cible ? cible.id : null,
      });
      for (const m of citees)
        Store.notifs.add(m.uid, {
          type: "mention",
          deId: ctx.moi.uid,
          deNom: ctx.moi.nom,
          dePhoto: ctx.moi.photo || "",
          postId: post.id,
          apercu: t.slice(0, 80),
        });
      Store.notifs.add(post.authorId, {
        type: "commentaire",
        deId: ctx.moi.uid,
        deNom: ctx.moi.nom,
        dePhoto: ctx.moi.photo || "",
        postId: post.id,
        apercu: t.slice(0, 80),
      });
    } catch (err) {
      toast(err.message || "Envoi impossible", "erreur");
      setTexte(t); // il n'est pas parti : on lui rend son texte
    }
  };

  return (
    <div className="commentaires">
      <div className="commentaires__liste">
        {!parents.length ? (
          <p className="texte-doux petit">Aucun commentaire. Encouragez ce membre 🙂</p>
        ) : (
          parents.map((c) => (
            <div key={c.id}>
              <Bulle
                c={c}
                ctx={ctx}
                effacable={c.authorId === ctx.moi.uid || post.authorId === ctx.moi.uid}
                onRepondre={viser}
                onSupprimer={supprimer}
                onAmen={amen}
                mienne={aReagi(c.id)}
              />
              {(enfants.get(c.id) || []).map((r) => (
                <Bulle
                  key={r.id}
                  c={r}
                  ctx={ctx}
                  enfant
                  effacable={r.authorId === ctx.moi.uid || post.authorId === ctx.moi.uid}
                  onRepondre={viser}
                  onSupprimer={supprimer}
                  onAmen={amen}
                  mienne={aReagi(r.id)}
                />
              ))}
            </div>
          ))
        )}
      </div>

      <ListeMentions propositions={mentions.propositions} onChoisir={(u) => mentions.choisir(u, champ.current)} />

      {reponseA && (
        <div className="commentaire-reponse">
          Réponse à <strong>{reponseA.nom}</strong>{" "}
          <button
            className="icon-btn icon-btn--mini"
            aria-label="Annuler"
            onClick={() => setReponseA(null)}
          >
            <Icone nom="fermer" taille={12} />
          </button>
        </div>
      )}

      <form className="commentaire-form" onSubmit={envoyer}>
        <Avatar membre={ctx.moi} taille={32} />
        <textarea
          ref={champ}
          rows={1}
          maxLength={1000}
          placeholder={reponseA ? `Répondre à ${reponseA.nom}…` : "Écrire un commentaire bienveillant…"}
          value={texte}
          onChange={(e) => {
            setTexte(e.target.value);
            ajuster(e.target);
            mentions.surSaisie(e.target);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              envoyer();
            }
          }}
        />
        <button className="icon-btn icon-btn--envoi" type="submit" aria-label="Envoyer">
          <Icone nom="envoyer" taille={17} />
        </button>
      </form>
    </div>
  );
}
